import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { defaultMonitoringProposalValidationResults } from '../constants/defaultMonitoringProposalValidationResults'
import { showErrorToast } from 'utils/toast'
import {
  requestValidateMonitoring,
  requestUpdateMonitoring,
  requestUpdateSiteResponse,
  getAboutMonitoring
} from 'api/monitoring'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'

export const MonitoringContext = React.createContext({})

export const MonitoringProvider = ({ children }) => {
  const params = useParams()
  const [monitoringProposalFieldValidation, setMonitoringProposalFieldValidation] = useState(
    defaultMonitoringProposalValidationResults
  )
  const [siteStateRecord, setSiteStateRecord] = useState(null)
  const [siteResponseRecord, setSiteResponseRecord] = useState(null)
  const [biodiversityFilter, setBiodiversityFilter] = useState(1)
  const [monitoring, setMonitoring] = useState({
    id: '',
    siteId: '',
    monitoringYear: null,
    monitoringStatusId: null,
    monitoringDataFiles: [],
    monitoringReportFiles: [],
    monitoringComments: '',
    createdAt: '',
    updatedAt: ''
  })
  const [isEditable, setIsEditable] = useState(true)
  const [checkPressureThreatsCompleted, setCheckPressureThreatsCompleted] = useState(false)

  useEffect(() => {
    const segments = window.location.pathname.split('/');
    if (segments.length > 5 && segments[5] !== 'pressure') {
      setCheckPressureThreatsCompleted(false);
    }
  }, [window.location.pathname]);

  useEffect(() => {
    const fetchMonitoringData = async () => {
      try {
        const effectiveMonitoringId = params?.monitoringId || params?.monitoring?.id || params?.id || ''

        if (!effectiveMonitoringId) return

        const res = await getAboutMonitoring(effectiveMonitoringId)
        if (res?.monitoring_status !== SITE_STATUS_DRAFT_ID || !res?.canUpdateMonitoring) {
          setIsEditable(false)
        } else {
          setIsEditable(true)
        }
        setMonitoring(res)
      } catch (error) {
        console.error('Error fetching monitoring data:', error)
      }
    }

    fetchMonitoringData()
  }, [params?.monitoringId, params?.monitoring, params?.id])

  // mergedParams combines route params and fetched monitoring data so pages
  // can access both from the MonitoringContext (e.g. { ...params, aboutMonitoring: monitoring })
  const mergedParams = React.useMemo(
    () => ({
      ...(params || {}),
      monitoring,
      monitoringId: params?.monitoringId || monitoring?.id || '',
      siteId: params?.siteId || monitoring?.siteId || ''
    }),
    [params, monitoring]
  )

  const getMonitoringValidation = async () => {
    try {
      const validateMonitoringRes = await requestValidateMonitoring(params.monitoringId)
      setMonitoringProposalFieldValidation({ ...validateMonitoringRes, updated: true })
      return validateMonitoringRes
    } catch (err) {
      showErrorToast(err && err.message ? err.message : String(err))
      throw err
    }
  }

  const updateMonitoring = async (requestBody) => {
    try {
      const res = await requestUpdateMonitoring(params.monitoringId, requestBody)
      if (res) {
        setMonitoring((m) => ({ ...m, ...res }))
      }
      return res
    } catch (err) {
      showErrorToast(err && err.message ? err.message : String(err))
      return {}
    }
  }

  const updateSiteResponse = async (response_id, requestBody) => {
    try {
      const res = await requestUpdateSiteResponse(response_id, requestBody)
      if (res) {
        setSiteResponseRecord(res)
      }
      return res
    } catch (err) {
      showErrorToast(err && err.message ? err.message : String(err))
      return {}
    }
  }

  const getUncompletedSection = (checkedThreat) => {
      setCheckPressureThreatsCompleted(checkedThreat);
  }


  const contextValue = React.useMemo(
    () => ({
      getMonitoringValidation,
      monitoringProposalFieldValidation,
      monitoring,
      mergedParams,
      updateMonitoring,
      siteStateRecord,
      setSiteStateRecord,
      siteResponseRecord,
      setSiteResponseRecord,
      updateSiteResponse,
      biodiversityFilter,
      setBiodiversityFilter,
      isEditable,
      getUncompletedSection,
      checkPressureThreatsCompleted
    }),
    [
      getMonitoringValidation,
      monitoringProposalFieldValidation,
      monitoring,
      mergedParams,
      updateMonitoring,
      siteStateRecord,
      siteResponseRecord,
      updateSiteResponse,
      biodiversityFilter,
      isEditable,
      getUncompletedSection,
      checkPressureThreatsCompleted
    ]
  )

  return <MonitoringContext.Provider value={contextValue}>{children}</MonitoringContext.Provider>
}

export default MonitoringProvider
